<template>
	<div v-if="isApproved" class="c-admin-stats-page">
		<PageHeader title="Admin">
			<template v-slot:title>Stats</template>
		</PageHeader>
		<Constrain>
			<AdminNav />
			<section class="c-admin-stats-page__card" :aria-busy="loading">
				<div class="c-admin-stats-page__header">
					<h2>Email-verified signups</h2>
					<div
						class="c-admin-stats-page__toggle"
						role="group"
						aria-label="Chart display"
					>
						<button
							type="button"
							class="btn"
							:class="{ 'btn-primary': !cumulative }"
							:aria-pressed="!cumulative"
							@click="cumulative = false"
						>
							Daily
						</button>
						<button
							type="button"
							class="btn"
							:class="{ 'btn-primary': cumulative }"
							:aria-pressed="cumulative"
							@click="cumulative = true"
						>
							Cumulative
						</button>
					</div>
				</div>
				<p>Workspace owners with verified emails, counted by signup date.</p>
				<div
					class="c-admin-stats-page__range"
					role="group"
					aria-label="Signup date range"
				>
					<button
						v-for="range in [30, 60, 180]"
						:key="range"
						type="button"
						class="btn"
						:class="{ 'btn-primary': dayCount === range }"
						:aria-pressed="dayCount === range"
						@click="dayCount = range"
					>
						{{ range }} days
					</button>
				</div>
				<p v-if="loading" role="status">Loading signup stats...</p>
				<div v-else-if="errorMessage" role="alert">
					<p>{{ errorMessage }}</p>
					<button type="button" class="btn btn-primary" @click="loadStats">
						Try again
					</button>
				</div>
				<template v-else-if="stats">
					<p class="c-admin-stats-page__total">
						<strong>{{ total }}</strong> signups in the last {{ dayCount }} days
					</p>
					<p>
						{{ stats.startDate }} – {{ stats.endDate }} ({{ stats.timezone }}).
						Today is incomplete.
					</p>
					<p v-if="total === 0" role="status">
						No email-verified signups in this period.
					</p>
					<AdminSignupChart :days="stats.days" :cumulative="cumulative" />
					<p v-if="cumulative" class="c-admin-stats-page__note">
						Cumulative count starts at the beginning of this {{ dayCount }}-day
						window.
					</p>
				</template>
			</section>
		</Constrain>
	</div>
	<Page404 v-else />
</template>

<script>
import { http } from '@swipekit/lib/http';
import Constrain from '@swipekit/components/utils/constrain.vue';
import PageHeader from '@swipekit/components/utils/page-header.vue';
import Page404 from './page404.vue';
import AdminNav from './admin-nav.vue';
import AdminSignupChart from './admin-signup-chart.vue';

export default {
	name: 'AdminStatsPage',
	components: { Constrain, PageHeader, Page404, AdminNav, AdminSignupChart },
	data() {
		return {
			stats: null,
			dayCount: 60,
			cumulative: false,
			loading: false,
			errorMessage: '',
			requestId: 0,
		};
	},
	computed: {
		isApproved() {
			const user = this.$store.getters['users/user'];
			return !!(
				user &&
				user.email &&
				user.email.toLowerCase() === 'shash@swipekit.app'
			);
		},
		total() {
			return this.stats
				? this.stats.days.reduce((total, day) => total + day.count, 0)
				: 0;
		},
	},
	watch: {
		dayCount() {
			this.loadStats();
		},
		isApproved: {
			immediate: true,
			handler(approved) {
				if (approved) {
					this.loadStats();
				} else {
					this.stats = null;
				}
			},
		},
	},
	methods: {
		async loadStats() {
			if (!this.isApproved) {
				return;
			}
			const requestId = ++this.requestId;
			this.loading = true;
			this.errorMessage = '';
			try {
				const response = await http.get('/admin/stats/signups', {
					params: { days: this.dayCount },
				});
				if (this.isApproved && requestId === this.requestId) {
					this.stats = response.data;
				}
			} catch (err) {
				if (requestId === this.requestId) {
					this.errorMessage = 'Could not load signup stats. Try again.';
				}
			} finally {
				if (requestId === this.requestId) {
					this.loading = false;
				}
			}
		},
	},
};
</script>

<style lang="scss">
.c-admin-stats-page {
	padding-bottom: var(--spacer);

	&__card {
		padding: var(--spacer-sm);
		border: 1px solid var(--color-bg-5);
		border-radius: 0.5rem;
		background: var(--color-bg-2);

		> p {
			font-size: var(--font-size-sm);
			margin-bottom: var(--margin-lg);
		}
	}

	&__header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		flex-wrap: wrap;
		gap: var(--margin-lg);
		margin-bottom: var(--margin-lg);

		h2 {
			font-size: 1.25rem;
			margin: 0;
		}
	}

	&__toggle {
		display: flex;
		gap: var(--margin-sm);
	}

	&__range {
		display: flex;
		flex-wrap: wrap;
		gap: var(--margin-sm);
		margin-bottom: var(--margin-lg);
	}

	&__total strong {
		font-size: 2rem;
		margin-right: var(--margin);
	}

	&__note {
		margin-top: var(--margin-lg);
	}
}
</style>
